import type { DataIndexUsageKey } from './types';

/**
 * Why an attribute cannot be deleted yet.
 *
 * The backend refuses to delete an attribute that is still referenced, so the
 * UI has to say which reference is in the way. The checks are evaluated in
 * display order and the first satisfied one is the message shown, which keeps
 * the drawer, the full page and the bulk-delete confirmation naming the same
 * blocker for the same attribute.
 */
export type AttributeDeleteBlockCheck = {
  when: boolean;
  reason: string;
};

/** The reason for the first satisfied check, or undefined when none apply. */
export function firstAttributeDeleteBlockReason(checks: AttributeDeleteBlockCheck[]): string | undefined {
  return checks.find((check) => check.when)?.reason;
}

/** Which content surfaces the caller renders, and so may block a delete. */
export type ContentUsageGates = {
  withTemplatesSnippetsLayouts: boolean;
  withDesignStudio: boolean;
};

const CONTENT_USAGE_BLOCKS: Array<{ key: DataIndexUsageKey; gate: keyof ContentUsageGates; reason: string }> = [
  {
    key: 'templates',
    gate: 'withTemplatesSnippetsLayouts',
    reason: 'This attribute is currently used in a template. To delete it, first remove it from all templates.',
  },
  {
    key: 'snippets',
    gate: 'withTemplatesSnippetsLayouts',
    reason: 'This attribute is currently used in a snippet. To delete it, first remove it from all snippets.',
  },
  {
    key: 'layouts',
    gate: 'withTemplatesSnippetsLayouts',
    reason: 'This attribute is currently used in a layout. To delete it, first remove it from all layouts.',
  },
  {
    key: 'design_studio',
    gate: 'withDesignStudio',
    reason: 'This attribute is currently used in Design Studio. To delete it, first remove it from Design Studio.',
  },
];

/**
 * The content half of the delete gate, in display order.
 *
 * A usage the caller does not render cannot be the reason it gives, so each
 * check is suppressed when its surface is hidden. Segments, campaigns and
 * newsletters are left to each caller: they disagree on whether archived
 * records count and on what to call a campaign.
 *
 * Usage arrives as records on a detail read and as counts on a list read, so
 * the caller passes a lookup rather than either shape — see
 * `usageCountsLookup` and `usageRecordsLookup`.
 */
export function attributeContentUsageDeleteChecks(
  countOf: (key: DataIndexUsageKey) => number,
  gates: ContentUsageGates,
): AttributeDeleteBlockCheck[] {
  return CONTENT_USAGE_BLOCKS.map(({ key, gate, reason }) => ({
    when: gates[gate] && countOf(key) !== 0,
    reason,
  }));
}
